/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect } from 'react';

import { useSpaceStore } from '@coze-foundation/space-store';
import {
  useAccountSettings as useBaseAccountSettings,
  UserInfoPanel,
} from '@coze-foundation/account-ui-base';
import { I18n } from '@coze-arch/i18n';

const getSpaceRoleLabel = (role?: number) => {
  const roleLabels: Record<number, Parameters<typeof I18n.t>[0]> = {
    1: 'space_members_role_owner',
    2: 'space_members_role_admin',
    3: 'space_members_role_member',
  };
  const label = role === undefined ? undefined : roleLabels[role];
  return label ? I18n.t(label) : '-';
};

const WorkspaceUserInfoPanel = () => {
  const selectedSpace = useSpaceStore(state => state.space);
  const spaceList = useSpaceStore(state => state.spaceList);
  const recentlyUsedSpaceList = useSpaceStore(
    state => state.recentlyUsedSpaceList,
  );
  const permissionState = useSpaceStore(state => state.menuPermissions);
  const fetchMenuPermissions = useSpaceStore(
    state => state.fetchMenuPermissions,
  );
  const currentSpace =
    spaceList.find(space => space.id === selectedSpace.id) ??
    recentlyUsedSpaceList.find(recentSpace =>
      spaceList.some(space => space.id === recentSpace.id),
    ) ??
    spaceList[0] ??
    selectedSpace;
  const currentSpaceWithRole = currentSpace as typeof currentSpace & {
    role_type?: number;
    space_role_type?: number;
  };
  const listedRole = Number(
    currentSpaceWithRole.role_type ?? currentSpaceWithRole.space_role_type,
  );

  useEffect(() => {
    if (
      !currentSpace.id ||
      permissionState?.space_id === currentSpace.id
    ) {
      return;
    }
    void fetchMenuPermissions(currentSpace.id).catch(() => undefined);
  }, [currentSpace.id, fetchMenuPermissions, permissionState?.space_id]);

  const currentRole =
    currentSpace.id && permissionState?.space_id === currentSpace.id
      ? permissionState.current_role
      : Number.isFinite(listedRole) && listedRole > 0
        ? listedRole
        : undefined;

  return (
    <UserInfoPanel
      workspaceName={currentSpace.name || '-'}
      workspaceRole={getSpaceRoleLabel(currentRole)}
    />
  );
};

export const useAccountSettings = () => {
  const tabs = [
    {
      id: 'account',
      tabName: I18n.t('menu_profile_account'),
      content: () => <WorkspaceUserInfoPanel />,
    },
  ];

  const { node, open } = useBaseAccountSettings({
    tabs,
  });
  return {
    node,
    open,
  };
};
